/**
 * East Asian Width, as a range table.
 *
 * Unicode property escapes cover `\p{Mn}`, `\p{Extended_Pictographic}` and
 * `\p{Regional_Indicator}` — but NOT East_Asian_Width, which is not a binary property and
 * has no escape. So the W/F set is a table. It is the only hand-maintained Unicode data in
 * CORE, it is derived from EastAsianWidth.txt (Unicode 15.1), and it is deliberately the
 * conservative reading: everything listed is Wide or Fullwidth, Ambiguous is treated as
 * half-width (the convention every subtitle tool uses, because Ambiguous is only wide in a
 * legacy CJK font context).
 *
 * Emoji are handled separately via `\p{Extended_Pictographic}`, so the ranges below stop at
 * the CJK/Kana/Hangul/fullwidth core plus the enclosed-CJK blocks.
 */

/** Sorted, non-overlapping [lo, hi] inclusive code-point ranges. */
const WIDE: readonly (readonly [number, number])[] = [
  [0x1100, 0x115f], // Hangul Jamo initial consonants
  [0x231a, 0x231b],
  [0x2329, 0x232a],
  [0x23e9, 0x23ec],
  [0x23f0, 0x23f0],
  [0x23f3, 0x23f3],
  [0x25fd, 0x25fe],
  [0x2614, 0x2615],
  [0x2648, 0x2653],
  [0x267f, 0x267f],
  [0x2693, 0x2693],
  [0x26a1, 0x26a1],
  [0x26aa, 0x26ab],
  [0x26bd, 0x26be],
  [0x26c4, 0x26c5],
  [0x26ce, 0x26ce],
  [0x26d4, 0x26d4],
  [0x26ea, 0x26ea],
  [0x26f2, 0x26f3],
  [0x26f5, 0x26f5],
  [0x26fa, 0x26fa],
  [0x26fd, 0x26fd],
  [0x2705, 0x2705],
  [0x270a, 0x270b],
  [0x2728, 0x2728],
  [0x274c, 0x274c],
  [0x274e, 0x274e],
  [0x2753, 0x2755],
  [0x2757, 0x2757],
  [0x2795, 0x2797],
  [0x27b0, 0x27b0],
  [0x27bf, 0x27bf],
  [0x2b1b, 0x2b1c],
  [0x2b50, 0x2b50],
  [0x2b55, 0x2b55],
  [0x2e80, 0x2e99],
  [0x2e9b, 0x2ef3],
  [0x2f00, 0x2fd5],
  [0x2ff0, 0x2ffb],
  [0x3000, 0x303e], // CJK symbols and punctuation, incl. ideographic space
  [0x3041, 0x3096], // Hiragana
  [0x3099, 0x30ff], // Katakana
  [0x3105, 0x312f], // Bopomofo
  [0x3131, 0x318e], // Hangul compatibility jamo
  [0x3190, 0x31e3],
  [0x31f0, 0x321e],
  [0x3220, 0x3247],
  [0x3250, 0x4dbf],
  [0x4e00, 0xa48c], // CJK unified ideographs
  [0xa490, 0xa4c6],
  [0xa960, 0xa97c], // Hangul jamo extended-A
  [0xac00, 0xd7a3], // Hangul syllables
  [0xf900, 0xfaff], // CJK compatibility ideographs
  [0xfe10, 0xfe19],
  [0xfe30, 0xfe52],
  [0xfe54, 0xfe66],
  [0xfe68, 0xfe6b],
  [0xff01, 0xff60], // fullwidth forms
  [0xffe0, 0xffe6],
  [0x16fe0, 0x16fe4],
  [0x16ff0, 0x16ff1],
  [0x17000, 0x187f7], // Tangut
  [0x18800, 0x18cd5],
  [0x18d00, 0x18d08],
  [0x1aff0, 0x1aff3],
  [0x1aff5, 0x1affb],
  [0x1affd, 0x1affe],
  [0x1b000, 0x1b122],
  [0x1b150, 0x1b152],
  [0x1b164, 0x1b167],
  [0x1b170, 0x1b2fb],
  [0x1f200, 0x1f202],
  [0x1f210, 0x1f23b],
  [0x1f240, 0x1f248],
  [0x1f250, 0x1f251],
  [0x1f260, 0x1f265],
  [0x20000, 0x2fffd], // CJK ext B–F
  [0x30000, 0x3fffd], // CJK ext G–
];

export function isWideCodePoint(cp: number): boolean {
  let lo = 0;
  let hi = WIDE.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const range = WIDE[mid] as readonly [number, number];
    if (cp < range[0]) hi = mid - 1;
    else if (cp > range[1]) lo = mid + 1;
    else return true;
  }
  return false;
}

/** True when the code point is CJK-ish enough that a joiner between it and its neighbour
 *  should be the CJK joiner rather than a space. Used by UnwrapOp/MergeDocumentsOp — the
 *  ops are not CORE's, but the predicate is shared, so it has exactly one owner. */
export function isCjkCodePoint(cp: number): boolean {
  return (
    (cp >= 0x2e80 && cp <= 0x303e) ||
    (cp >= 0x3041 && cp <= 0x33ff) ||
    (cp >= 0x3400 && cp <= 0x4dbf) ||
    (cp >= 0x4e00 && cp <= 0x9fff) ||
    (cp >= 0xa960 && cp <= 0xa97c) ||
    (cp >= 0xac00 && cp <= 0xd7a3) ||
    (cp >= 0xf900 && cp <= 0xfaff) ||
    (cp >= 0xfe30 && cp <= 0xfe4f) ||
    (cp >= 0xff01 && cp <= 0xff60) ||
    (cp >= 0x20000 && cp <= 0x3fffd)
  );
}

/**
 * Combining marks contribute zero width; a cluster made only of them is zero-width.
 *
 * biome-ignore lint/suspicious/noMisleadingCharacterClass: judged and kept. The rule warns that
 * a character class matches CODE POINTS, so a multi-codepoint grapheme inside one matches its
 * parts. Nothing in this class is multi-codepoint \u2014 U+200D and U+FE00\u2013U+FE0F are single code
 * points \u2014 and the class is only ever tested against a WHOLE cluster that has already failed
 * the two emoji tests below. Every multi-codepoint grapheme the rule is about (ZWJ families,
 * skin tones, flags, keycaps, emoji-presentation sequences) returns before reaching it.
 * Verified by measurement, not by reading: all five score their expected width.
 */
export const COMBINING_RE = /^[\p{Mn}\p{Me}\u200D\uFE00-\uFE0F]+$/u;
export const HAS_PICTOGRAPHIC_RE = /\p{Extended_Pictographic}/u;
export const HAS_REGIONAL_INDICATOR_RE = /\p{Regional_Indicator}/u;

/**
 * U+FE0E VARIATION SELECTOR-15 \u2014 the TEXT presentation selector.
 *
 * Found while judging the rule above, and it is a real width error rather than a style one:
 * `\u2764\uFE0E` is a text-presentation heart and occupies ONE cell, but it contains an
 * Extended_Pictographic code point, so the emoji test claimed two. Its sibling U+FE0F (emoji
 * presentation) correctly stays at two. Narrow, but width feeds line breaking and CPS, so a
 * wrong cell count propagates into every reflow decision on the line.
 */
export const TEXT_PRESENTATION_SELECTOR = '\uFE0E';

export function hasTextPresentation(cluster: string): boolean {
  return cluster.includes(TEXT_PRESENTATION_SELECTOR);
}
